// src/modules/app-views/invoices/components/InvoicePreviewBody.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Space, Divider, Collapse, Tooltip, theme } from 'antd';
import {
  ShoppingCartOutlined,
  DollarOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicePreviewBody = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { selectedDocument } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!selectedDocument) return null;

  const items = selectedDocument?.items || [];
  const payments = selectedDocument?.payment?.methods || [];
  const totals = selectedDocument?.totals || {};

  // Nature of operation
  const operationNature = translateX(selectedDocument?.operationNature) || null;

  // Order number (can be array with multiple orders)
  const rawOrder = selectedDocument?.orderNumber;
  const rawLegacy = selectedDocument?.orderLegacyCode;
  const ordersArray = Array.isArray(rawOrder)
    ? rawOrder
    : rawOrder
      ? [rawOrder]
      : Array.isArray(rawLegacy)
        ? rawLegacy
        : [];
  const orderNumberLabel = buildOrderNumberLabel(ordersArray);
  const orderNumberRaw = ordersArray.length > 0 ? ordersArray.join(', ') : null;

  // Seller name
  const sellerName = selectedDocument?.sellerName || selectedDocument?.seller?.name || null;

  // Document ID and access key
  const documentId = selectedDocument?._id || null;
  const accessKey = selectedDocument?.accessKey || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const collapseItems = [
    // Items Section
    {
      key: 'items',
      label: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
        >
          <ShoppingCartOutlined />
          {translateX('items')} ({items.length})
        </span>
      ),
      children:
        items.length === 0 ? (
          <Text type="secondary">{translateX('no_items_added')}</Text>
        ) : (
          <div>
            {items.map((item, index) => (
              <div
                key={item._id || index}
                style={{
                  padding: '8px 0',
                  borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {index + 1}. {item?.product?.name || item?.description || '—'}
                    </Text>
                    {item?.product?.sku && (
                      <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                        SKU: {item.product.sku}
                      </Text>
                    )}
                  </div>
                  <Text strong style={{ whiteSpace: 'nowrap' }}>
                    {financialService.formatCurrency(item?.totalValue || 0)}
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {item?.quantity || 0} x {financialService.formatCurrency(item?.unitValue || 0)}
                  </Text>
                  {item?.discountValue > 0 && (
                    <Text type="success" style={{ fontSize: 12 }}>
                      -{financialService.formatCurrency(item.discountValue)}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        ),
    },

    // Totals Section
    {
      key: 'totals',
      label: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
        >
          <DollarOutlined />
          {translateX('totals')}
        </span>
      ),
      children: (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          <TotalRow label={translateX('products')} value={totals.productsAmount} />
          {totals.discountAmount > 0 && (
            <TotalRow label={translateX('discount')} value={-totals.discountAmount} isDiscount />
          )}
          {totals.freightAmount > 0 && (
            <TotalRow label={translateX('freight')} value={totals.freightAmount} />
          )}
          {totals.insuranceAmount > 0 && (
            <TotalRow label={translateX('insurance')} value={totals.insuranceAmount} />
          )}
          {totals.otherExpensesAmount > 0 && (
            <TotalRow label={translateX('other_expenses')} value={totals.otherExpensesAmount} />
          )}
          <Divider style={{ margin: '8px 0' }} />
          <TotalRow label={translateX('total_invoice')} value={totals.documentAmount} isTotal />
        </Space>
      ),
    },

    // Payments Section
    {
      key: 'payments',
      label: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
        >
          <CreditCardOutlined />
          {translateX('payments')} ({payments.length})
        </span>
      ),
      children:
        payments.length === 0 ? (
          <Text type="secondary">{translateX('no_payments_added')}</Text>
        ) : (
          <div>
            {payments.map((payment, index) => (
              <div
                key={payment._id || index}
                style={{
                  padding: '8px 0',
                  borderBottom: index < payments.length - 1 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Text>
                  {index + 1}. {getPaymentMethodLabel(payment)}
                </Text>
                <Text strong>{financialService.formatCurrency(payment?.amount || 0)}</Text>
              </div>
            ))}
          </div>
        ),
    },

    // Additional Info Section
    {
      key: 'info',
      label: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
        >
          <InfoCircleOutlined />
          {translateX('additional_info')}
        </span>
      ),
      children: (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          {/* Nature of Operation */}
          <InfoRow label={translateX('nature_of_operation')} value={operationNature} />

          {/* Order Number - with copy */}
          {orderNumberRaw && (
            <InfoRowWithCopy
              label={translateX('order_number')}
              value={orderNumberLabel}
              copyValue={orderNumberRaw}
              copyMessage="data_copied"
              colorPrimary={token.colorPrimary}
            />
          )}

          {/* Seller Name */}
          <InfoRow label={translateX('seller')} value={sellerName} />

          {/* Operation Type */}
          <InfoRow
            label={translateX('operation_type')}
            value={
              selectedDocument?.operationType === 'exit'
                ? translateX('operation_exit')
                : translateX('operation_entry')
            }
          />

          {/* Environment */}
          <InfoRow
            label={translateX('environment')}
            value={
              selectedDocument?.environment === 'production'
                ? translateX('production')
                : translateX('homologation')
            }
          />

          {/* Document ID - with copy and ellipsis */}
          {/*{documentId && (*/}
          {/*  <InfoRowWithCopy*/}
          {/*    label="ID"*/}
          {/*    value={documentId}*/}
          {/*    copyMessage="data_copied"*/}
          {/*    ellipsis*/}
          {/*    colorPrimary={token.colorPrimary}*/}
          {/*  />*/}
          {/*)}*/}
        </Space>
      ),
    },
  ];

  return (
    <Collapse
      ghost
      items={collapseItems}
      size="small"
      style={{ paddingLeft: 0 }}
      styles={{ header: { padding: '4px 0' } }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicePreviewBody;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Total row component
 */
function TotalRow({ label, value, isTotal, isDiscount }) {
  if (value == null) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text type={isTotal ? undefined : 'secondary'} strong={isTotal}>
        {label}:
      </Text>
      <Text
        strong={isTotal}
        style={{
          color: isDiscount
            ? '#52c41a'
            : isTotal
              ? undefined //'#1890ff'
              : undefined,
          fontSize: isTotal ? 15 : undefined,
        }}
      >
        {financialService.formatCurrency(value)}
      </Text>
    </div>
  );
}

/**
 * Info row component
 */
function InfoRow({ label, value, strong = false }) {
  if (!value) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text type="secondary">{label}:</Text>
      <Text strong={strong}>{value}</Text>
    </div>
  );
}

/**
 * Info row component with copy to clipboard
 * Copy icon is placed to the LEFT of the value
 */
function InfoRowWithCopy({
  label,
  value,
  copyValue,
  copyMessage,
  ellipsis = false,
  strong = false,
  colorPrimary,
}) {
  if (!value) return null;

  const handleCopy = () => {
    appService.handleCopyToClipboard(copyValue || value, copyMessage);
  };

  const valueStyle = ellipsis
    ? {
        maxWidth: 180,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }
    : {};

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
        {label}:
      </Text>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Tooltip title={translateX('copy')}>
          <CopyOutlined
            style={{
              cursor: 'pointer',
              marginLeft: 8,
              color: colorPrimary,
              fontSize: 12,
              flexShrink: 0,
            }}
            onClick={handleCopy}
          />
        </Tooltip>
        {typeof value === 'string' ? (
          <Tooltip title={ellipsis ? value : undefined}>
            <Text style={valueStyle} strong={strong}>
              {value}
            </Text>
          </Tooltip>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

/**
 * Build order number label with badge for multiple orders
 * @param {Array} ordersArray - Array of order numbers
 * @returns {React.ReactNode|null} Formatted label with badge or null
 */
function buildOrderNumberLabel(ordersArray) {
  if (ordersArray.length === 0) return null;

  const firstOrder = ordersArray[0];
  const extraCount = ordersArray.length - 1;

  // Build tooltip with all orders if there are multiple
  const tooltipContent = ordersArray.length > 1 ? ordersArray.join(', ') : null;

  const content = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{firstOrder}</span>
      {extraCount > 0 && (
        <span
          style={{
            backgroundColor: '#1890ff',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            padding: '0 6px',
            borderRadius: 10,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
          }}
        >
          +{extraCount}
        </span>
      )}
    </span>
  );

  // Wrap with tooltip if there are multiple orders
  if (tooltipContent) {
    return <Tooltip title={tooltipContent}>{content}</Tooltip>;
  }

  return content;
}

/**
 * Get payment method label from NF-e type or code
 * @param {Object} method - Payment method object
 * @returns {string} Payment method label
 */
function getPaymentMethodLabel(method) {
  if (!method) return translateX('payment');

  // Payment type labels (NF-e standard)
  const typeLabels = {
    MONEY: 'Dinheiro',
    CASH: 'Dinheiro',
    CHECK: 'Cheque',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    STORE_CREDIT: 'Crédito Loja',
    FOOD_VOUCHER: 'Vale Alimentação',
    MEAL_VOUCHER: 'Vale Refeição',
    GIFT_CARD: 'Vale Presente',
    FUEL_VOUCHER: 'Vale Combustível',
    TRADE_BILL: 'Duplicata Mercantil',
    BANK_SLIP: 'Boleto Bancário',
    BANK_DEPOSIT: 'Depósito Bancário',
    PIX: 'PIX',
    TRANSFER: 'Transferência',
    CASHBACK: 'Cashback',
    NO_PAYMENT: 'Sem Pagamento',
    OTHER: 'Outros',
  };

  // Try type label first
  if (method.type && typeLabels[method.type]) {
    return typeLabels[method.type];
  }

  // Fallback to code-based labels (NF-e codes)
  const codeLabels = {
    '01': 'Dinheiro',
    '02': 'Cheque',
    '03': 'Cartão de Crédito',
    '04': 'Cartão de Débito',
    '05': 'Crédito Loja',
    10: 'Vale Alimentação',
    11: 'Vale Refeição',
    12: 'Vale Presente',
    13: 'Vale Combustível',
    14: 'Duplicata Mercantil',
    15: 'Boleto Bancário',
    16: 'Depósito Bancário',
    17: 'PIX',
    18: 'Transferência',
    19: 'Cashback',
    90: 'Sem Pagamento',
    99: 'Outros',
  };

  if (method.code && codeLabels[method.code]) {
    return codeLabels[method.code];
  }

  return method.description || method.type || translateX('payment');
}
